import { forwardRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AiScriptSpreadsheetViewer,
  type AiScriptSpreadsheetViewerHandle,
  type AiScriptSpreadsheetViewerProps,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer";
import { useGetAiScriptSpreadsheet } from "@/payroll/ai-script/query";
import {
  getPayBreakdownApprovalUnit,
  getPayBreakdownSheetName,
  getPayBreakdownSheetId,
  withPayBreakdownSheetId,
} from "./routes";

type PayBreakdownSpreadsheetViewerProps = Omit<
  AiScriptSpreadsheetViewerProps,
  "activeSheetId" | "onActiveSheetChange"
>;

/**
 * First visible tab belonging to a department, for `?approval_unit=<access_key>`.
 *
 * Returns null when the key matches nothing this viewer can see — the workbook
 * then opens on its first tab, which for an approver scoped to one department is
 * already theirs.
 *
 * @param accessKey [string, null] the department's access key from the URL
 * @param sheets [Array, undefined] tabs visible to this user
 * @return [string, null] the tab to open, or null to leave the choice alone
 */
const resolveApprovalUnitSheetId = (
  accessKey: string | null,
  sheets: { id: string; access_key: string | null }[] | undefined,
): string | null => {
  if (!accessKey || !sheets) return null;
  return sheets.find((sheet) => sheet.access_key === accessKey)?.id ?? null;
};

// Names are unique within a workbook and regenerated identically each run, so this
// is the one sheet reference that survives a rerun at every business — access keys
// only exist where someone configured them.
const resolveNamedSheetId = (
  sheetName: string | null,
  sheets: { id: string; name: string }[] | undefined,
): string | null => {
  if (!sheetName || !sheets) return null;
  return sheets.find((sheet) => sheet.name === sheetName)?.id ?? null;
};

export const PayBreakdownSpreadsheetViewer = forwardRef<
  AiScriptSpreadsheetViewerHandle,
  PayBreakdownSpreadsheetViewerProps
>(function PayBreakdownSpreadsheetViewer(props, ref) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Already in the query cache — the viewer below loads the same key, so this
  // resolves the department link without a second request.
  const { data: spreadsheetData } = useGetAiScriptSpreadsheet(
    props.businessId,
    props.runId,
  );
  const activeSheetId =
    getPayBreakdownSheetId(searchParams) ??
    resolveApprovalUnitSheetId(
      getPayBreakdownApprovalUnit(searchParams),
      spreadsheetData?.spreadsheet.sheets,
    ) ??
    resolveNamedSheetId(
      getPayBreakdownSheetName(searchParams),
      spreadsheetData?.spreadsheet.sheets,
    );
  const setActiveSheetId = useCallback(
    (sheetId: string) => {
      setSearchParams(
        (currentSearchParams) =>
          withPayBreakdownSheetId(currentSearchParams, sheetId),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <AiScriptSpreadsheetViewer
      commentsEnabled
      {...props}
      ref={ref}
      activeSheetId={activeSheetId}
      onActiveSheetChange={setActiveSheetId}
    />
  );
});
